import type { OnInit } from '@angular/core';
import type { ElementRef } from '@angular/core';
import { DestroyRef, Directive, computed, effect, inject, input, signal, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import type { Observable } from 'rxjs';
import type { SelectedEntity } from '@common/types/layout.type';
import { EDrawerType, ESelectedEntityType } from '@common/types/layout.type';
import type { HoverState } from '@web/layout/content/page-content/directives/single-hover.directive';
import type { DenormalizedColumnEntity, DenormalizedSectionEntity } from '@web/types';
import { type DenormalizedBlockEntity } from '@web/types';
import { filter, map, take, withLatestFrom } from 'rxjs/operators';

import type { BlockForm } from '@sites/entity-signal-form';
import { isBlockHiddenOnPage } from '@sites/entity-core';

import { LibraryBlocksService } from '@web/store/library-blocks/library-blocks.service';

import { BlocksFacade } from '@web/store/page-structure/blocks/blocks.facade';
import { LayoutFacade } from '@web/store/layout/layout.facade';
import { SiteFacade } from '@web/store/website/site/site.facade';
import { LibraryBlockFacade } from '@web/store/library-blocks/library-blocks.facade';
import { UserFacade } from '@web/store/user/user.facade';
import { AppFacade } from '@web/store/app/app.facade';
import { NavigationDrawerService } from '@web/layout/drawers/navigation-drawer/navigation-drawer.service';
import { RouterFacade } from '@web/store/router/router.facade';
import { WebBlockFormService } from '@web/core/services/web-block-form.service';

import { BlockMeasureScheduler } from './block-measure.scheduler';

import type { ItemControlsPlacement } from './item-controls-placement.type';
import { NO_ITEM_CONTROLS } from './item-controls-placement.type';
import type { Device } from '@sites/entity-core';

/** The three breakpoints, in the order the single-breakpoint icons are named. */
const ONE_OF = [['mobile'], ['tablet'], ['desktop']] as const;

/** Below these the controls have no room where they normally sit. */
const PADDING_TOP_THRESHOLD = 75;
const PADDING_BOTTOM_THRESHOLD = 40;
const MINIMAL_CONTENT_HEIGHT = 200;

@Directive({})
export abstract class BaseBlockComponent<
    T extends DenormalizedBlockEntity = DenormalizedBlockEntity,
> implements OnInit {
    public readonly entity = input.required<T>();

    /**
     * The two neighbours on the page, for the gap and shared-block decorations.
     * Named for what they are rather than `prev`/`nextEntity`: `entity` is this
     * component's own, the way every settings panel spells it, and these two are
     * somebody else's.
     *
     * Typed as the whole block union rather than `T`: a neighbour is whatever
     * subtype happens to sit next to this block, not another of its own kind.
     * Only the settings all three share are read off them.
     *
     * They stay entity inputs rather than forms: this component only glances at
     * them, and a form per neighbour would mean three registry entries per block
     * on screen.
     */
    public readonly previousSibling = input<DenormalizedBlockEntity | null>(null);
    public readonly nextSibling = input<DenormalizedBlockEntity | null>(null);
    public readonly index = input.required<number>();
    public readonly isFirst = input<boolean>(false);
    public readonly isLast = input<boolean>(false);

    protected readonly blocksFacade: BlocksFacade = inject(BlocksFacade);
    protected readonly siteFacade: SiteFacade = inject(SiteFacade);
    protected readonly libraryBlockFacade: LibraryBlockFacade = inject(LibraryBlockFacade);
    protected readonly layoutFacade: LayoutFacade = inject(LayoutFacade);
    protected readonly userFacade: UserFacade = inject(UserFacade);
    protected readonly appFacade: AppFacade = inject(AppFacade);
    protected readonly libraryBlocksService: LibraryBlocksService = inject(LibraryBlocksService);
    protected readonly navDrawerService: NavigationDrawerService = inject(NavigationDrawerService);
    protected readonly routerFacade: RouterFacade = inject(RouterFacade);
    protected readonly blockFormService = inject(WebBlockFormService);
    protected readonly destroyRef: DestroyRef = inject(DestroyRef);
    private readonly measureScheduler = inject(BlockMeasureScheduler);

    /** The element that holds the block's rows — see {@link measureItemControls}. */
    private readonly blockRoot = viewChild.required<ElementRef<HTMLElement>>('blockRoot');

    /**
     * The block's own state is read here, not off the `block` input: the form is
     * the single writer, so an edit made through it shows up before the store
     * round-trip — and a read of one leaf wakes only the bindings on that leaf.
     *
     * Assigned in `ngOnInit` rather than a field initializer: creating a form runs
     * an `effect()`, which throws NG0602 inside a reactive context, and the
     * required input has no value during construction.
     */
    protected form!: BlockForm;

    public ngOnInit(): void {
        this.form = this.blockFormService.getOrCreateScoped(this.entity().id, this.destroyRef);
    }

    /** Sticks the block to the top or the bottom of the viewport while scrolling. */
    public readonly isPinnedTop = computed<boolean>(() => this.form?.pin().value() === 'top');
    public readonly isPinnedBottom = computed<boolean>(() => this.form?.pin().value() === 'bottom');

    /** Which breakpoints the block is switched off on. */
    public readonly hidden = computed<{ desktop: boolean; tablet: boolean; mobile: boolean }>(
        () => this.form?.hidden().value() ?? { desktop: false, tablet: false, mobile: false }
    );

    /** Write desktop style edits through to the tablet and mobile tabs. */
    public readonly syncWithDesktop = computed<boolean>(() => Boolean(this.form?.syncWithDesktop().value()));

    public readonly isSettingsMenuOpened = signal<boolean>(false);
    public readonly hoverState = signal<HoverState>(null);

    /**
     * The rendered height of `.block`. A signal because the DOM is the one input
     * here that is not one already — everything else the placement depends on is
     * read straight off the form.
     */
    private readonly renderedHeight = signal<number>(0);

    constructor() {
        // Re-measure whenever a setting changes the box, and after it has settled
        // rather than during the binding.
        effect((onCleanup) => {
            if (!this.form) {
                return;
            }

            this.form.padding().value();
            this.form.width().value();
            // And whenever the rows change: a widget added or removed anywhere
            // inside the block changes its height without touching any of the
            // block's own settings, and the placement classes would keep the
            // height they were measured at until the next padding edit or reload.
            // The store hands out a fresh entity for the whole subtree on any edit
            // within it, which is what the wrapper's `ngOnChanges` used to react to.
            this.entity();

            // Through the shared scheduler rather than this block's own timer, so
            // that a page-wide change costs one layout pass instead of one per
            // block — see {@link BlockMeasureScheduler}.
            this.measureScheduler.schedule(this, () =>
                this.renderedHeight.set(this.blockRoot().nativeElement.clientHeight)
            );

            // Cancelled on the next run and on destroy: the measurement reads
            // `viewChild.required`, which throws once the view is gone.
            onCleanup(() => this.measureScheduler.cancel(this));
        });
    }

    /**
     * Where this block's hover controls sit: a shallow or full-bleed block has no
     * room for them inside itself, so they move onto its edge or outside it.
     *
     * It belongs to the host and not to the view, because the host is what binds
     * the classes.
     *
     * {@link renderedHeight} is measured on `.block` with `clientHeight`: that
     * element carries a 2px bottom border, and only `clientHeight` on it leaves
     * exactly the height the rows render at — which is what this threshold has
     * always compared.
     */
    public readonly itemControls = computed<ItemControlsPlacement>(() => {
        // Read first, so the computed still tracks it on the run where the form
        // is not built yet — otherwise it would cache the default and never
        // recompute.
        const isShallow = this.renderedHeight() < MINIMAL_CONTENT_HEIGHT;

        if (!this.form) {
            return NO_ITEM_CONTROLS;
        }

        const isFullWidth = this.form.width().value()?.unit === 'vw';
        const padding = this.form.padding().value() as { desktop?: { value?: { top?: number; bottom?: number } } };

        const hasSmallTop = Number(padding?.desktop?.value?.top ?? 0) < PADDING_TOP_THRESHOLD;
        const hasSmallBottom = Number(padding?.desktop?.value?.bottom ?? 0) < PADDING_BOTTOM_THRESHOLD;

        return {
            rowControls: isFullWidth || isShallow,
            basicControlsPosition: !hasSmallTop || !isFullWidth,
            smallTopIndent: hasSmallTop && !isFullWidth,
            smallBottomIndent: hasSmallBottom && !isFullWidth,
            bottomInnerControlsPosition: hasSmallTop && !hasSmallBottom && isFullWidth,
            bottomOuterControlsPosition: hasSmallTop && hasSmallBottom && isFullWidth,
        };
    });

    public readonly isAdmin$: Observable<boolean> = this.appFacade.isAdmin$;
    public readonly entityId$: Observable<string> = this.layoutFacade.entityId$;
    public readonly hideControls$: Observable<boolean> = this.layoutFacade.hideControls$;
    /**
     * §7's "preview mode": while true, `hovered()` below drops every mouse-driven hover/threshold
     * state instead of recording it, so the hover outline and the "+ add block" edge buttons — all
     * driven off {@link hoverState} — never appear, without touching the dozen `hoverState()?.x`
     * reads scattered through this component's and its siblings' templates.
     */
    protected readonly isCanvasPreviewActive = toSignal(this.layoutFacade.isCanvasPreviewActive$, {
        initialValue: false,
    });
    protected readonly isDeviceMobile = toSignal(this.layoutFacade.isDeviceMobile$, { initialValue: false });
    protected readonly isDeviceTablet = toSignal(this.layoutFacade.isDeviceTablet$, { initialValue: false });

    private readonly routerParams = toSignal(this.routerFacade.routerParams$);

    /** Which breakpoint the editor is previewing, and therefore edits. */
    public readonly device = computed<Device>(() => {
        if (this.isDeviceMobile()) {
            return 'mobile';
        }

        return this.isDeviceTablet() ? 'tablet' : 'desktop';
    });

    /** Whether the block is switched off on the breakpoint being previewed. */
    public readonly isHiddenOnView = computed<boolean>(() => Boolean(this.hidden()[this.device()]));

    /** The hint shown in place of a block hidden on the breakpoint being previewed. */
    public readonly hiddenBlockHintText = computed<string>(() => `control_block_hidden_${this.device()}`);

    public hovered(event: HoverState): void {
        this.hoverState.set(this.isCanvasPreviewActive() ? null : event);
    }

    public addBlockTop(): void {
        this.layoutFacade.openDrawer({ drawerType: EDrawerType.block, blockIndex: this.index() });
    }

    public addBlockBottom(): void {
        this.layoutFacade.openDrawer({ drawerType: EDrawerType.block, blockIndex: this.index() + 1 });
    }

    public copyBlock(): void {
        this.blocksFacade.copyBlock(this.entity(), this.index() + 1);
    }

    public removeBlock(): void {
        this.blocksFacade.removeBlock(this.entity(), this.index());
    }

    public moveUp(): void {
        this.blocksFacade.moveUp(this.index());
    }

    public moveDown(): void {
        this.blocksFacade.moveDown(this.index());
    }

    public selectBlock(): void {
        this.layoutFacade.setSelectedEntity({ entityType: ESelectedEntityType.block, entityId: this.entity().id });
        this.navDrawerService.selectInNavigation({ entityId: this.entity().id });
    }

    public copyBlockToPage(): void {
        this.blocksFacade.copyToPage(this.entity());
    }

    public saveBlockToGallery(): void {
        this.appFacade.locale$.pipe(take(1), withLatestFrom(this.userFacade.userId$)).subscribe(([lang, userId]) => {
            return this.libraryBlockFacade.upsertLibraryBlock(
                this.libraryBlocksService.createDefaultBlockEntity({ userId, lang, blockId: this.entity().id })
            );
        });
    }

    public setSettingsMenuOpened(state: boolean): void {
        this.isSettingsMenuOpened.set(state);
    }

    public hasSectionActiveColumn$(section: DenormalizedSectionEntity): Observable<boolean> {
        return this.layoutFacade.selectedEntity$.pipe(
            filter(
                (selectedEntity: SelectedEntity) =>
                    selectedEntity?.entityType === ESelectedEntityType.column && Boolean(selectedEntity.entityId)
            ),
            map(({ entityId }: SelectedEntity) =>
                section.children.some((column: DenormalizedColumnEntity) => column.id === entityId)
            )
        );
    }

    /**
     * The label a block carries when it is switched off somewhere — one icon for
     * a single breakpoint, a combined one for any mix. Lifted here from the
     * default and zero blocks, which held identical copies.
     */
    public readonly hiddenIconPath = computed<string>(() => {
        const { desktop, tablet, mobile } = this.hidden();
        const only = ONE_OF.find(([device]) => this.isOnly(device));

        if (only) {
            return `./assets/img/websites/lpc-hidden-${only[0]}-label-icon.svg`;
        }

        return desktop || tablet || mobile ? './assets/img/websites/lpc-hidden-all-label-icon.svg' : '';
    });

    public readonly hiddenIconTooltip = computed<string>(() => {
        const { desktop, tablet, mobile } = this.hidden();
        const on = (['mobile', 'tablet', 'desktop'] as const).filter((device) => this.hidden()[device]);

        if (!on.length) {
            return '';
        }
        if (on.length === 1) {
            return `control_block_hidden_${on[0]}`;
        }
        if (desktop && tablet && mobile) {
            return 'control_block_hidden_to_all';
        }

        // Two of three, named in the order the translation keys use.
        return `control_block_hidden_${on[0]}_${on[1]}`;
    });

    private isOnly(device: Device): boolean {
        const state = this.hidden();

        return (['desktop', 'tablet', 'mobile'] as const).every((each) => state[each] === (each === device));
    }

    /** Identity, so it comes off the entity — the form never renames a block. */
    public readonly displayId = computed<string>(() => this.entity().id);

    public readonly isPreviousSiblingPinnedToTop = computed<boolean>(() => this.previousSibling()?.props.pin === 'top');
    public readonly isNextSiblingPinnedToBottom = computed<boolean>(() => this.nextSibling()?.props.pin === 'bottom');

    public readonly isPreviousSiblingShared = computed<boolean>(() =>
        Boolean(this.previousSibling()?.meta?.isShared || this.previousSibling()?.meta?.role)
    );
    public readonly isNextSiblingShared = computed<boolean>(() =>
        Boolean(this.nextSibling()?.meta?.isShared || this.nextSibling()?.meta?.role)
    );

    /* ---------------------------- Shared block role --------------------------- */

    /**
     * The page being edited. One reader for the whole hierarchy: hiding a shared
     * block, showing it again, and the flag below all need it, and every block
     * component used to re-derive it from `routerParams$` by hand.
     */
    protected readonly pageId = computed<string | undefined>(() => this.routerParams()?.pageId);

    /** Which role a shared block plays — a header sits at the top, a footer at the bottom. */
    protected readonly sharedType = computed<string | undefined>(() => this.entity().meta.role);

    /**
     * Whether this shared block is switched off on the page being edited.
     *
     * Depends on the entity as well as the route: hiding or showing a block only
     * writes `meta.hiddenOnPages`, so a flag derived from the route alone
     * never refreshes until a navigation.
     */
    public readonly isBlockHidden = computed<boolean>(() => isBlockHiddenOnPage(this.entity(), this.pageId()));

    public readonly canAddTop = computed<boolean>(() =>
        this.entity().meta.isShared ? this.sharedType() === 'footer' : !this.isPinnedTop()
    );

    public readonly canAddBottom = computed<boolean>(() =>
        this.entity().meta.isShared ? this.sharedType() === 'header' : !this.isPinnedBottom()
    );

    public readonly hiddenBlockText = computed<string>(() =>
        this.sharedType() === 'header' ? 'shared_block_header_hidden' : 'shared_block_footer_hidden'
    );

    public readonly hintText = computed<string>(() =>
        this.sharedType() === 'header' ? 'shared_block_header_hint' : 'shared_block_footer_hint'
    );

    public hideBlock(): void {
        const pageId = this.pageId();

        if (pageId) {
            this.blocksFacade.hideBlock(this.entity().id, pageId);
        }
    }

    public showBlock(): void {
        const pageId = this.pageId();

        if (pageId) {
            this.blocksFacade.showBlock(this.entity().id, pageId);
        }
    }
}
